import { useCallback, useRef, useState } from "react"

import { toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { invalidateHomeQueries } from "@/lib/home-query-cache"

import {
  isPersistedTestSetId,
  mapDeleteTestSetInputToTransport,
  useDeleteTestSetTransport,
  useInvalidateTestSetTransportQueries,
} from "../../transport"

interface UseTestSetDeleteActionParams {
  /** Runs once the delete has succeeded, e.g. to close a dialog or navigate. */
  onDeleted?: () => void
}

/**
 * Deletes a test set and reports the outcome. Shared by the editor's header
 * menu, which then leaves for the list, and the list's own row menu, which
 * closes its dialog. Home reads the test-set count, so both invalidation sets
 * run together.
 *
 * `isDeleting` stays true until the whole sequence has landed, not just the
 * mutation: the invalidation behind it refetches the list and the home queries,
 * and the toast and any navigation come after that. The mutation's own pending
 * flag drops at the first of those steps and would hand back a live confirm
 * button mid-sequence.
 */
export function useTestSetDeleteAction({
  onDeleted,
}: UseTestSetDeleteActionParams = {}) {
  const { trackEvent } = useAnalytics()
  const queryClient = useQueryClient()
  const deleteTestSetMutation = useDeleteTestSetTransport()
  const invalidateTestSetTransportQueries =
    useInvalidateTestSetTransportQueries()
  const isDeletingRef = useRef(false)
  const [isFinishing, setIsFinishing] = useState(false)

  const deleteTestSet = useCallback(
    async (testSetId?: string): Promise<void> => {
      if (!testSetId) {
        return
      }
      // An unsaved editor reaches here with the route's `new` sentinel in place
      // of an id; there is nothing on the server under it to delete.
      if (!isPersistedTestSetId(testSetId)) {
        return
      }
      if (isDeletingRef.current) {
        return
      }
      isDeletingRef.current = true
      setIsFinishing(true)

      try {
        await deleteTestSetMutation.mutateAsync(
          mapDeleteTestSetInputToTransport({ testSetId }),
        )
        await Promise.all([
          invalidateHomeQueries(queryClient),
          invalidateTestSetTransportQueries(),
        ])
        trackEvent(TRACKING_EVENTS.TEST_SET.DELETED, {
          test_set_id: testSetId,
        })
        toast.success({
          title: "Success",
          description: "Test set deleted successfully",
        })
        onDeleted?.()
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : "Failed to delete test set. Please try again."
        trackEvent(TRACKING_EVENTS.TEST_SET.DELETE_FAILED, {
          error: description,
          testSetId,
        })
        toast.danger({ title: "Delete Failed", description })
      } finally {
        isDeletingRef.current = false
        setIsFinishing(false)
      }
    },
    [
      deleteTestSetMutation,
      invalidateTestSetTransportQueries,
      onDeleted,
      queryClient,
      trackEvent,
    ],
  )

  // See the note in useTestSetCloneAction: the pending state belongs to the
  // observer that runs the mutation, so it is returned from here.
  return {
    deleteTestSet,
    isDeleting: deleteTestSetMutation.isPending || isFinishing,
  }
}
